"use client";

import { FullTag, Tag, createSkeletonShimmer } from "@dafiti/closet-ui";
import { NoProductImage } from "@dafiti/closet-ui/icons";
import { cn } from "@dafiti/closet-ui/utils";
import { useTranslations } from "next-intl";
import { ReactNode, RefObject, useEffect, useRef, useState } from "react";

export type ProductCardProps<T extends React.ElementType = "div"> = {
  as?: T;
  ref?: RefObject<T>;
} & React.ComponentPropsWithoutRef<T>;

const ProductCard = <T extends React.ElementType = "div">({
  as,
  className,
  ...props
}: ProductCardProps<T>) => {
  const Component = as ?? "div";
  return (
    <Component
      {...props}
      className={cn("w-[183px] relative block", className)}
    />
  );
};

export type ProductCardImageProps = {
  src: string;
  alt: string;
  loading?: "lazy" | "eager";
  containerClassName?: string;
  className?: string;
  fallback?: ReactNode;
} & Omit<React.ComponentPropsWithoutRef<"img">, "src" | "alt" | "loading">;

const ProductCardImage = ({
  src,
  alt,
  loading,
  className,
  containerClassName,
  fallback,
  ...props
}: ProductCardImageProps) => {
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Handles images that already failed before React hydrated (SSR + browser cache)
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    if (img.complete && img.naturalWidth === 0) {
      setHasError(true);
    }
  }, []);

  const containerClass = cn(
    "w-full h-auto overflow-hidden rounded-lg aspect-[131/190] flex justify-center items-center relative",
    containerClassName,
  );

  const resolvedFallback = fallback ?? (
    <NoProductImage className="w-8 h-8 text-gray-400" aria-label={alt} />
  );

  if (hasError) {
    return (
      <div
        className={cn(
          containerClass,
          "bg-gray-100 min-h-[150px] lg:min-h-[246px]",
        )}
        data-testid="product-card-image"
      >
        {resolvedFallback}
      </div>
    );
  }

  return (
    <div className={containerClass} data-testid="product-card-image">
      <span
        className="absolute top-0 left-0 size-full bg-black opacity-[0.04]"
        aria-hidden="true"
        tabIndex={-1}
      />
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading={loading}
        width={262}
        height={380}
        {...props}
        className={cn("w-full", createSkeletonShimmer(), className)}
        onError={() => setHasError(true)}
      />
    </div>
  );
};

export type ProductCardBodyProps<T extends React.ElementType = "div"> = {
  as?: T;
} & React.ComponentPropsWithoutRef<T>;

const ProductCardBody = <T extends React.ElementType = "div">({
  as,
  ...props
}: ProductCardBodyProps<T>) => {
  const Component = as ?? "div";
  return <Component {...props} />;
};

export type ProductCardBrandProps<T extends React.ElementType = "span"> = {
  as?: T;
} & React.ComponentPropsWithoutRef<T>;

const ProductCardBrand = <T extends React.ElementType = "span">({
  as,
  className,
  ...props
}: ProductCardBrandProps<T>) => {
  const Component = as ?? "span";

  return (
    <Component
      {...props}
      className={cn("text-caption font-medium text-gray-500 block", className)}
    />
  );
};

export type ProductCardNameProps<T extends React.ElementType = "span"> = {
  as?: T;
} & React.ComponentPropsWithoutRef<T>;

const ProductCardName = <T extends React.ElementType = "p">({
  as,
  className,
  ...props
}: ProductCardNameProps<T>) => {
  const Component = as ?? "p";

  return (
    <Component
      {...props}
      className={cn("text-body-3 font-normal text-black", className)}
    />
  );
};

export type ProductCardDiscountTag<T extends React.ElementType = "span"> = {
  as?: T;
  discount: number | undefined;
} & React.ComponentPropsWithoutRef<T>;

const ProductCardDiscountTag = <T extends React.ElementType = "span">({
  as,
  className,
  discount,
  ...props
}: ProductCardDiscountTag<T>) => {
  const Component = as ?? "span";

  if (!discount || isNaN(discount)) return null;

  return (
    <Component
      {...props}
      className={cn(
        "font-bold text-white text-3xs rounded-full w-[40px] h-[24px] bg-danger-200 flex items-center justify-center",
        className,
      )}
    >
      -{Math.abs(discount)}%
    </Component>
  );
};

export type ProductCardSponsoredTagProps<T extends React.ElementType = "span"> =
  {
    as?: T;
  } & React.ComponentPropsWithoutRef<T>;

const ProductCardSponsoredTag = <T extends React.ElementType = "p">({
  as,
  className,
  ...props
}: ProductCardSponsoredTagProps<T>) => {
  const Component = as ?? "p";
  const t = useTranslations("shared");

  return (
    <Component
      {...props}
      className={cn("text-caption font-medium text-gray-400", className)}
    >
      {t("productCard.sponsored")}
    </Component>
  );
};

export type ProductCardFreeShippingTagProps<
  T extends React.ElementType = "span",
> = {} & React.ComponentPropsWithoutRef<T>;

const ProductCardFreeShippingTag = <T extends React.ElementType = "span">({
  className,
  ...props
}: ProductCardFreeShippingTagProps<T>) => {
  const t = useTranslations("shared");
  return (
    <Tag
      {...props}
      as="span"
      color="positive"
      className={cn(
        "bg-positive-300 w-[84px] h-[16px] lg:h-[24px] leading-normal px-0",
        className,
      )}
    >
      {t("productCard.freeShipping")}
    </Tag>
  );
};

export type ProductCardFullTagProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<"span">;

const ProductCardFullTag = ({
  className,
  ...props
}: ProductCardFullTagProps) => (
  <span {...props} className={cn("inline-block", className)}>
    <FullTag size="sm" color="green" className="h-[16px] lg:h-[18px]" />
  </span>
);

export type ProductCardNewTagProps<T extends React.ElementType = "span"> = {
  as?: T;
} & React.ComponentPropsWithoutRef<T>;

const ProductCardNewTag = <T extends React.ElementType = "span">({
  as,
  className,
  ...props
}: ProductCardNewTagProps<T>) => {
  const Component = as ?? "span";
  const t = useTranslations("shared");

  return (
    <Component
      {...props}
      className={cn("text-caption font-bold text-support-200", className)}
    >
      {t("productCard.new")}
    </Component>
  );
};

export {
  ProductCard,
  ProductCardBody,
  ProductCardBrand,
  ProductCardDiscountTag,
  ProductCardFreeShippingTag,
  ProductCardFullTag,
  ProductCardImage,
  ProductCardName,
  ProductCardNewTag,
  ProductCardSponsoredTag,
};
